import { Component, Input } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { NzUploadFile } from "ng-zorro-antd/upload";
import { NgxSpinnerService } from "ngx-spinner";
import { Observable, Subscription } from "rxjs";
import { IOrganization } from "src/app/dtos";
import { MessageService } from "src/app/services/message.service";
import { NotificationService } from "src/app/services/notification.service";
import { ProfileService } from "src/app/services/profile.service";
import { MAX_DESCRIPTION_LENGTH, REGEX, CATEGORY_SELECT_STYLE, NOTIFICATION_KEYWORDS } from "../../../constants";

@Component({
	selector: "app-general-settings",
	templateUrl: "./general-settings.component.html",
	styleUrls: ["./general-settings.component.scss"]
})

export class GeneralSettingsComponent {
	maxDescription: number = MAX_DESCRIPTION_LENGTH;
	formGroup: FormGroup;
	organization: IOrganization;
	private subscriptions: Subscription[] = [];
	dropDownStyle = CATEGORY_SELECT_STYLE;
	profileUrl = [];
	previewImage: string;
	isUploadProfilePictureModalVisible: boolean = false;
	profilePictureList = [];
	fileData;
	fileToUpload: NzUploadFile;
	fileSizeError: boolean;
	fileTypeError:boolean=false;
	maxfilesize = 1024*1024;
	removedProfilePicture: boolean = false;
	updatedProfilePicture: boolean = false;


	constructor(
		private profileService: ProfileService,
		private fb: FormBuilder,
		private messageService: MessageService,
		private spinnerService: NgxSpinnerService,
		private notificationService: NotificationService
	) {
		this.profileService = profileService;
		this.fb = fb;

		this.profileService.loadProfile();
	}

	ngOnInit() {
		let sb = this.profileService.lastSelectedOrganization$.subscribe(
			(org: IOrganization) => {
				this.organization = org;
				this.loadForm();
				if(org['profilePicture']) {
					this.profileUrl[0] = {
						fileName : org['profilePicture']
					};
				}
				this.messageService.sendMessage({ organizationDetails: this.organization, type: this.organization.type });
			}, err => {
				if (err) {
					this.profileService.loadProfile();
					this.organization = this.profileService.currentOrganization;
				}
			}
		);

		this.subscriptions.push(sb);
	}

	loadForm(): void {
		this.formGroup = this.fb.group({
			description: new FormControl(this.organization.description, Validators.maxLength(MAX_DESCRIPTION_LENGTH)),
			commercialRegisterNumber: new FormControl(this.organization.commercialRegisterNumber, Validators.maxLength(250)),
			email: new FormControl(this.organization.email, Validators.email),
			website: new FormControl(this.organization.website, [ Validators.required,Validators.maxLength(250)]),
			vat: new FormControl(this.organization.vat, [Validators.maxLength(250)]),
			primaryContact: new FormControl(this.organization.primaryContact, Validators.maxLength(250)),
			termsAndConditions: new FormControl(this.organization.termsAndConditions, [Validators.maxLength(250)]),
			paymentTerms: new FormControl(this.organization.paymentTerms),
			deliveryTerms:new FormControl(this.organization.deliveryTerms),
		});
	}

	updateProfileInformation(): void {
		this.spinnerService.show();
		for (const i in this.formGroup.controls) {
			this.formGroup.controls[i].markAsDirty();
			this.formGroup.controls[i].updateValueAndValidity();
		}

		if (!this.formGroup.valid) {
			return;
		}

		// const payload = this.prepareForm();

		const formData = this.formGroup.value;
		let payload = {
			id: this.organization.id,
			description: formData.description,
			email: formData.email,
			commercialRegisterNumber: formData.commercialRegisterNumber,
			website: formData.website,
			vat: formData.vat,
			primaryContact: formData.primaryContact,
			termsAndConditions: formData.termsAndConditions,
			paymentTerms:formData.paymentTerms,
			deliveryTerms:formData.deliveryTerms
		};

		const payloadData = new FormData();

		if (this.profilePictureList?.length) {
			this.profilePictureList.forEach((file: any) => {
				payloadData.append("file", file);
			});
		}

		if (this.removedProfilePicture) {
			payload['removeProfilePicture'] = true;
		}

		payloadData.append("data", JSON.stringify(payload));

		this.profileService.updateMyOrganization(payload.id, payloadData).subscribe(
			(profile) => {
				this.spinnerService.hide();
				this.profileUrl = [];
				this.profilePictureList = [];
				this.updatedProfilePicture = false;
				this.removedProfilePicture = false;
				this.previewImage = '';
				this.profileService.loadProfile();
				this.notificationService.successNotification("profileUpdate");
			}, (error) => {
				this.spinnerService.hide();
				this.profileService.loadProfile();
				this.notificationService.failureNotification(NOTIFICATION_KEYWORDS.TRY_AGAIN);
			}
		);
	}

	/**
	 * Open upload profile picture modal
	 */
	openUploadProfilePictureModal() {
		this.isUploadProfilePictureModalVisible = true;
	}

	/**
	 * Close upload profile picture modal
	 */
	closeUploadProfilePictureModal() {
		this.isUploadProfilePictureModalVisible = false;
	}

	/**
	 * Update organizations profile picture
	 * @returns
	 */
	updateProfilePicture() {
		this.profileUrl = [];
		this.updatedProfilePicture = true;
		this.isUploadProfilePictureModalVisible = false;
		this.removedProfilePicture = false;
	}

	/**
	 * Handle before upload file event
	 * @param file : file to upload
	 */
	beforeUpload = (file: NzUploadFile) => {
		this.fileToUpload = file;
		this.fileSizeError = false;
        
		const valid_type=["img/jpg",
		"img/png",
		"image/jpeg",
		"image/png",];

		if(!valid_type.includes(file.type))
		{
			this.fileTypeError=true;
			this.fileSizeError=false;
			return false;
		}

		if (file.size > this.maxfilesize) {
	
			this.fileSizeError = true;
			this.fileTypeError=false;
			return false;
		}

		this.getBase64(file as any).then(x => {
			this.previewImage = x?.toString() as any;
		});

		this.profilePictureList = this.profilePictureList.concat(file);
		this.fileTypeError=false;
		return false;
	};


	getBase64 = (file): Promise<string | ArrayBuffer | null> =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = (error) => reject(error);
		});

	removeImage() {
		this.profileUrl = [];
		this.profilePictureList = [];
		this.updatedProfilePicture = false;
		this.removedProfilePicture = true;
	}

	handleRemoveUploaded = () => {
		this.profilePictureList = [];
		this.previewImage = "";
		this.updatedProfilePicture = false;
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach(sb => sb.unsubscribe());
	}

	// helpers for View
	isControlValid(controlName: string): boolean {
		const control = this.formGroup.controls[controlName];
		return control.valid && (control.dirty || control.touched);
	}

	isControlInvalid(controlName: string): boolean {
		const control = this.formGroup.controls[controlName];
		return control.invalid && (control.dirty || control.touched);
	}

	controlHasError(validation, controlName): boolean {
		const control = this.formGroup.controls[controlName];
		return control.hasError(validation) && (control.dirty || control.touched);
	}

	isControlTouched(controlName): boolean {
		const control = this.formGroup.controls[controlName];
		return control.dirty || control.touched;
	}

	selectPrimaryContact(value) {
		this.formGroup.get('primaryContact').setValue(value);
		this.formGroup.markAsTouched();
		this.formGroup.markAsDirty();
	}
}
